import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Mail, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion";
import { SarvaMark } from "@/components/SarvaMark";
import { questions, dims, bands, scoreAssessment } from "@/data/assessment";
import { api, usePageMeta } from "@/lib/api";

export default function AssessmentPage() {
  usePageMeta(
    "ESG & Sustainability Readiness Assessment | SarvaRitam",
    "Free 12-question ESG readiness assessment — score your maturity across governance, environment, social, climate, reporting and data."
  );
  const [step, setStep] = useState(0); // 0..11 questions, 12 = results
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(null);
  const [sending, setSending] = useState(false);

  const results = useMemo(() => (step === 12 ? scoreAssessment(answers) : null), [step, answers]);
  const q = questions[step];

  const choose = (v) => {
    setAnswers((a) => ({ ...a, [q.id]: v }));
    setTimeout(() => setStep((s) => Math.min(s + 1, 12)), 220);
  };

  const sendResults = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await api.post("/assessment/results", {
        email, name, overall: results.overall, band: results.band,
        e: results.e, s: results.s, g: results.g,
        climate: results.climate, disclosure: results.disclosure, data: results.data,
        priorities: results.priorities.map((p) => p.title),
      });
      setSent(true);
    } catch (err) {
      setSent(false);
    } finally {
      setSending(false);
    }
  };

  const progress = Math.round((Math.min(step, 12) / 12) * 100);

  return (
    <section className="bg-charcoal text-offwhite min-h-screen pt-32 pb-44 relative grain" data-testid="assessment-page">
      <div className="wrap relative max-w-3xl">
        <div className="text-center mb-12">
          <p className="eyebrow text-gold flex items-center justify-center gap-3 mb-6">
            <img src="/brand/mark-white.png" alt="" className="w-5 h-5 object-contain" /> ESG Readiness Assessment
          </p>
          {step < 12 ? (
            <h1 className="h-display font-semibold text-3xl sm:text-4xl lg:text-5xl">
              Where does your organisation <span className="serif-it text-gold">stand?</span>
            </h1>
          ) : (
            <h1 className="h-display font-semibold text-3xl sm:text-4xl lg:text-5xl">
              Your ESG <span className="serif-it text-gold">readiness profile.</span>
            </h1>
          )}
        </div>

        {step < 12 && (
          <div className="mb-12">
            <div className="flex items-center justify-between font-mono text-[10.5px] uppercase tracking-widest2 text-sage mb-3">
              <span data-testid="assessment-progress-label">Question {step + 1} / {questions.length} · {q.dim}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1 bg-white/10">
              <motion.div className="h-full bg-gold" animate={{ width: `${progress}%` }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} data-testid="assessment-progress-bar" />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step < 12 ? (
            <motion.div key={q.id} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} data-testid="assessment-question">
              <h2 className="h-display font-semibold text-2xl sm:text-3xl leading-snug mb-10">{q.q}</h2>
              <div className="grid gap-3" role="radiogroup" aria-label={q.q}>
                {q.options.map((opt, i) => (
                  <button
                    key={opt}
                    onClick={() => choose(i)}
                    className={`group text-left border px-6 py-5 text-[15px] font-semibold transition-all duration-200 flex items-center justify-between ${
                      answers[q.id] === i ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/94 hover:border-gold/60 hover:bg-white/[0.03]"
                    }`}
                    data-testid={`assessment-option-${q.id}-${i}`}
                    role="radio"
                    aria-checked={answers[q.id] === i}
                  >
                    {opt}
                    <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ml-4 ${answers[q.id] === i ? "border-gold bg-gold text-charcoal" : "border-white/25 group-hover:border-gold"}`}>
                      {answers[q.id] === i && <Check size={12} />}
                    </span>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep((s) => s - 1)} className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest2 text-sage hover:text-gold transition-colors" data-testid="assessment-back-button">
                  <ArrowLeft size={13} /> Previous question
                </button>
              )}
            </motion.div>
          ) : (
            <motion.div key="results" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="assessment-results">
              {/* Overall */}
              <div className="bg-forest p-8 sm:p-12 text-center relative overflow-hidden mb-6">
                <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(198,161,91,0.5), transparent 70%)" }} />
                <div className="relative">
                  <p className="eyebrow text-gold mb-5">Overall score</p>
                  <p className="font-display font-bold text-[72px] sm:text-[96px] leading-none" data-testid="assessment-overall-score">
                    {results.overall}<span className="text-[28px] text-white/70 font-semibold">/100</span>
                  </p>
                  <p className="serif-it text-gold text-2xl mt-3" data-testid="assessment-band">{results.band}</p>
                  <p className="text-white/80 text-[13.5px] mt-3 max-w-md mx-auto leading-relaxed">{results.bandNote}</p>
                </div>
              </div>

              {/* Dimension scores */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10" data-testid="assessment-dimensions">
                {[
                  ["E — Environmental", results.e], ["S — Social", results.s], ["G — Governance", results.g],
                  ["Climate readiness", results.climate], ["Disclosure readiness", results.disclosure], ["Data readiness", results.data],
                ].map(([label, v]) => (
                  <div key={label} className="border rule-dark bg-white/[0.03] p-5">
                    <p className="text-[11px] font-bold text-sage leading-tight min-h-[28px]">{label}</p>
                    <div className="h-1.5 bg-white/10 mt-3">
                      <motion.div className="h-full bg-gold" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
                    </div>
                    <p className="font-display font-bold text-xl mt-2.5">{v}<span className="text-[11px] text-white/60 font-semibold">/100</span></p>
                  </div>
                ))}
              </div>

              {/* Priorities */}
              <div className="mb-12">
                <p className="eyebrow text-gold mb-6">Your top 3 ESG priorities</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {results.priorities.map((p, i) => (
                    <div key={p.dim} className="border rule-dark bg-white/[0.03] p-6" data-testid={`assessment-priority-${i}`}>
                      <span className="font-mono text-[11px] text-gold">{String(i + 1).padStart(2, "0")}</span>
                      <p className="font-display font-semibold text-[16px] mt-3 leading-snug">{p.title}</p>
                      <p className="text-[11px] font-mono text-sage mt-2">Focus: {p.dim}</p>
                      <Link to={p.service} className="text-[12px] font-bold text-gold mt-4 inline-block u-sweep">Explore service</Link>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email capture */}
              <div className="border rule-dark bg-white/[0.03] p-8 sm:p-10" data-testid="assessment-email-capture">
                {sent === true ? (
                  <div className="text-center py-4" data-testid="assessment-email-success">
                    <span className="w-12 h-12 rounded-full bg-gold text-charcoal flex items-center justify-center mx-auto mb-5"><Check size={22} /></span>
                    <p className="font-display font-semibold text-xl">Sent. Check your inbox.</p>
                    <p className="text-[13.5px] text-white/75 mt-3 leading-relaxed">Your results have been emailed to {email}. The email includes your scores, band and suggested priorities.</p>
                  </div>
                ) : (
                  <>
                    <p className="eyebrow text-gold mb-3">Get Your ESG Roadmap</p>
                    <h2 className="h-display font-semibold text-2xl mb-3">Email me these results</h2>
                    <p className="text-[13.5px] text-white/75 mb-7 leading-relaxed">Receive a summary of your scores, band and suggested priorities by email.</p>
                    {sent === false && <p className="text-[13px] text-gold mb-4" data-testid="assessment-email-error">We couldn't send the email right now — your results are still shown above. Please try again later.</p>}
                    <form onSubmit={sendResults} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="bg-white/[0.05] border rule-dark px-4 py-3.5 text-[14px] text-offwhite placeholder:text-white/55 focus:outline-none focus:border-gold/60" data-testid="assessment-name-input" aria-label="Your name" />
                      <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="bg-white/[0.05] border rule-dark px-4 py-3.5 text-[14px] text-offwhite placeholder:text-white/55 focus:outline-none focus:border-gold/60" data-testid="assessment-email-input" aria-label="Email address" />
                      <button type="submit" disabled={sending} className="btn-gold justify-center" data-testid="assessment-send-button">
                        <Mail size={15} /> {sending ? "Sending…" : "Send results"}
                      </button>
                    </form>
                  </>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/contact" className="btn-gold flex-1 justify-center py-4" data-testid="assessment-roadmap-cta">
                  Discuss Your ESG & Sustainability Roadmap <ArrowUpRight size={15} />
                </Link>
                <button onClick={() => { setAnswers({}); setStep(0); setSent(null); }} className="btn-ghost-light flex-1 justify-center py-4" data-testid="assessment-restart-button">
                  Retake the assessment
                </button>
              </div>
              <p className="text-[11.5px] text-white/60 mt-6 text-center leading-relaxed">
                Results are indicative, based on your responses, and do not constitute legal or assurance advice.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
